import { API_URL } from '../../../../constants/urls.constants';
import type {
  CommonResponse,
  FailedJobResponse,
  FailedJobsParams,
  GetQueryArgsType,
} from '../../../../types/global.types';
import { appServiceApi } from '../../../apis/app-service.api';
import { APP_API_TAGS } from '../../../app.constants';
import type {
  AllAttendanceReportsData,
  AllAttendanceReportsStatisticsData,
  AllFailedShiftData,
  AttendanceData,
  AttendanceDetails,
  AttendanceParams,
  FailedAttendanceListParams,
  FailedAttendanceListResponse,
  FailedShiftsListParams,
  FailedShiftsListResponse,
  FailedShiftAssignListParams,
  FailedShiftAssignListResponse,
  ShiftAssignData,
  ShiftAssignMetaData,
  ShiftAssignParams,
  ShiftData,
  ShiftParams,
  WorkingReportData,
  WorkingReportParams,
} from './emp-attendance.type';

const { SHIFTS, ATTENDANCES, ALL_FAILED_SHIFT, ATTENDANCE_REPORTS } = APP_API_TAGS || {};

export const empAttendanceApi = appServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    // ------ Start: API Endpoints for Shift ------
    createShift: builder.mutation({
      query: (body) => {
        return {
          url: API_URL.shifts,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: (res) => (res ? [SHIFTS] : []),
    }),
    createShiftBulk: builder.mutation({
      query: (body) => {
        return {
          url: `${API_URL.shifts}/bulk`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: (res) => (res ? [SHIFTS] : []),
    }),
    downloadShiftExcel: builder.query<string, void>({
      query: () => ({
        url: `${API_URL.shifts}/upload/template`,
        responseHandler: async (response: Response) => {
          const blob = await response.blob();
          return window.URL.createObjectURL(blob);
        },
        cache: 'no-cache',
      }),
    }),
    uploadShift: builder.mutation<CommonResponse<any>, FormData>({
      query: (body) => {
        return {
          url: `${API_URL.shifts}/upload`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: (res) => (res ? [SHIFTS, ALL_FAILED_SHIFT] : []),
    }),
    getAllShifts: builder.query<
      CommonResponse<{ data: ShiftData[]; meta: ShiftAssignMetaData }>,
      GetQueryArgsType<ShiftParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.shifts,
        ...queryArgs,
      }),
      providesTags: [SHIFTS],
    }),
    getShiftDetails: builder.query<CommonResponse<ShiftData>, string>({
      query: (id) => ({
        url: `${API_URL.shifts}/${id}`,
      }),
      providesTags: [SHIFTS],
    }),
    updateShift: builder.mutation({
      query: ({ data, id }) => {
        return {
          url: `${API_URL.shifts}/${id}`,
          method: 'PATCH',
          body: data,
        };
      },
      invalidatesTags: (res) => {
        return res ? [SHIFTS] : [];
      },
    }),
    deleteShift: builder.mutation({
      query: ({ id }) => ({
        url: `${API_URL.shifts}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (res) => {
        return res ? [SHIFTS] : [];
      },
    }),
    // ------ END: API Endpoints for Shift ------

    // ------ Start: API Endpoints for Shift Assign ------
    assignShiftSingleUser: builder.mutation({
      query: (body) => {
        return {
          url: `${API_URL.shifts}/assign`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: (res) => (res ? [SHIFTS] : []),
    }),
    unAssignShiftSingleUser: builder.mutation({
      query: (body) => {
        return {
          url: `${API_URL.shifts}/assign/unassign-user`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: (res) => (res ? [SHIFTS] : []),
    }),
    assignShiftMultipleUser: builder.mutation({
      query: (body) => {
        return {
          url: `${API_URL.shifts}/assign/bulk-users`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: (res) => (res ? [SHIFTS] : []),
    }),
    assignMultipleShiftMultipleUser: builder.mutation({
      query: (body) => {
        return {
          url: `${API_URL.shifts}/assign/bulk`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: (res) => (res ? [SHIFTS] : []),
    }),
    getTenantUsersWithShiftAssignments: builder.query<
      CommonResponse<{ data: ShiftAssignData[]; total: number }>,
      GetQueryArgsType<ShiftAssignParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.shifts}/tenant-users/assignments`,
        ...queryArgs,
      }),
      providesTags: [SHIFTS],
    }),
    getFailedShiftsJobs: builder.query<
      CommonResponse<FailedJobResponse>,
      GetQueryArgsType<FailedJobsParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.shifts}/failed-shifts/jobs`,
        ...queryArgs,
      }),
      providesTags: [SHIFTS],
    }),
    getFailedShiftsList: builder.query<
      CommonResponse<FailedShiftsListResponse>,
      GetQueryArgsType<FailedShiftsListParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.shifts}/failed-shifts/list`,
        ...queryArgs,
      }),
      providesTags: [SHIFTS],
    }),
    // ------ END: API Endpoints for Shift Assign ------

    // ------ START: API Endpoints for Working time report ------
    getWorkingReport: builder.query<
      CommonResponse<WorkingReportData>,
      GetQueryArgsType<WorkingReportParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.attendance}/working-report`,
        ...queryArgs,
      }),
      providesTags: [ATTENDANCES],
    }),
    // ------ END: API Endpoints for Working time report ------

    // ------ START: API Endpoints for Attendance ------
    getAllAttendance: builder.query<
      CommonResponse<AttendanceData>,
      GetQueryArgsType<AttendanceParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.attendance}/attendance-list`,
        ...queryArgs,
      }),
      providesTags: [ATTENDANCES],
    }),
    getAttendanceDetails: builder.query<
      CommonResponse<AttendanceDetails>,
      string
    >({
      query: (id) => ({
        url: `${API_URL.attendance}/attendance/details/${id}`,
      }),
      providesTags: [ATTENDANCES],
    }),
    // ------ END: API Endpoints for Attendance ------

    // ------ START: API Endpoints for Bulk Shift Error ------
    getAllFailedShift: builder.query<
      AllFailedShiftData,
      { params: { skip: number; limit: number; organizationId?: string } }
    >({
      query: (queryArgs) => ({
        url: `${API_URL.shifts}/assign/failed-bulk-import`,
        ...queryArgs,
      }),
      providesTags: [ALL_FAILED_SHIFT],
    }),
    // ------ END: API Endpoints for Bulk Shift Error ------

    // -------- Start: API Attendance Report ------------
    attendanceGenerateReport: builder.mutation({
      query: (body) => {
        return {
          url: `${API_URL.reports}/generate-report/attendance`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: (res) => (res ? [ATTENDANCE_REPORTS] : []),
    }),

    getAttendanceReports: builder.query<
      AllAttendanceReportsData,
      { params: { page: number; limit: number } }
    >({
      query: (queryArgs) => ({
        url: `${API_URL.reports}/list`,
        ...queryArgs,
      }),
      providesTags: [ATTENDANCE_REPORTS],
    }),

    getAttendanceReportsStatistics: builder.query<
      AllAttendanceReportsStatisticsData,
      { params: { startDate: string; endDate: string } }
    >({
      query: (queryArgs) => ({
        url: `${API_URL.reports}/stats`,
        ...queryArgs,
      }),
      providesTags: [ATTENDANCE_REPORTS],
    }),
    // -------- End: API Attendance Report ------------

    // -------- Start: API Bulk Attendance Upload ------------
    downloadAttendanceTemplate: builder.query<string, void>({
      query: () => ({
        url: `${API_URL.attendance}/upload/template`,
        responseHandler: async (response: Response) => {
          const blob = await response.blob();
          return window.URL.createObjectURL(blob);
        },
        cache: 'no-cache',
      }),
    }),
    uploadAttendance: builder.mutation<CommonResponse<any>, FormData>({
      query: (body) => ({
        url: `${API_URL.attendance}/upload-attendance`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (res) => (res ? [ATTENDANCES] : []),
    }),
    getFailedAttendanceJobs: builder.query<
      CommonResponse<FailedJobResponse>,
      GetQueryArgsType<FailedJobsParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.attendance}/failed-attendance/jobs`,
        ...queryArgs,
      }),
      providesTags: [ATTENDANCES],
    }),
    getFailedAttendanceRecords: builder.query<
      CommonResponse<FailedAttendanceListResponse>,
      GetQueryArgsType<FailedAttendanceListParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.attendance}/failed-attendance/list`,
        ...queryArgs,
      }),
      providesTags: [ATTENDANCES],
    }),
    // -------- End: API Bulk Attendance Upload ------------

    // -------- Start: API Bulk Shift Assign ------------
    downloadShiftAssignTemplate: builder.query<string, void>({
      query: () => ({
        url: `${API_URL.shifts}/assign/upload/template`,
        responseHandler: async (response: Response) => {
          const blob = await response.blob();
          return window.URL.createObjectURL(blob);
        },
        cache: 'no-cache',
      }),
    }),
    uploadShiftAssign: builder.mutation<CommonResponse<any>, FormData>({
      query: (body) => ({
        url: `${API_URL.shifts}/assign/upload`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (res) => (res ? [SHIFTS] : []),
    }),
    getFailedShiftAssignJobs: builder.query<
      CommonResponse<FailedJobResponse>,
      GetQueryArgsType<FailedJobsParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.shifts}/assign/failed-import/jobs`,
        ...queryArgs,
      }),
      providesTags: [SHIFTS],
    }),
    getFailedShiftAssignRecords: builder.query<
      CommonResponse<FailedShiftAssignListResponse>,
      GetQueryArgsType<FailedShiftAssignListParams>
    >({
      query: (queryArgs) => ({
        url: `${API_URL.shifts}/assign/failed-import/list`,
        ...queryArgs,
      }),
      providesTags: [SHIFTS],
    }),
    // -------- End: API Bulk Shift Assign ------------
  }),
});

export const {
  // API Hooks for Shift
  useCreateShiftMutation,
  useCreateShiftBulkMutation,
  useLazyDownloadShiftExcelQuery,
  useUploadShiftMutation,
  useGetAllShiftsQuery,
  useGetShiftDetailsQuery,
  useUpdateShiftMutation,
  useDeleteShiftMutation,
  useGetAllFailedShiftQuery,
  useGetFailedShiftsJobsQuery,
  useGetFailedShiftsListQuery,

  // API Hooks for Shift Assign
  useAssignShiftSingleUserMutation,
  useUnAssignShiftSingleUserMutation,
  useAssignShiftMultipleUserMutation,
  useAssignMultipleShiftMultipleUserMutation,
  useGetTenantUsersWithShiftAssignmentsQuery,

  // API Hooks for Working time report
  useGetWorkingReportQuery,

  // API Hooks for Attendance
  useGetAllAttendanceQuery,
  useGetAttendanceDetailsQuery,

  // API Hooks for Attendance Report
  useAttendanceGenerateReportMutation,
  useGetAttendanceReportsQuery,
  useGetAttendanceReportsStatisticsQuery,

  // API Hooks for Bulk Attendance Upload
  useLazyDownloadAttendanceTemplateQuery,
  useUploadAttendanceMutation,
  useGetFailedAttendanceJobsQuery,
  useGetFailedAttendanceRecordsQuery,

  // API Hooks for Bulk Shift Assign
  useLazyDownloadShiftAssignTemplateQuery,
  useUploadShiftAssignMutation,
  useGetFailedShiftAssignJobsQuery,
  useGetFailedShiftAssignRecordsQuery,
} = empAttendanceApi;
